import { useRef, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Upload, Star, Trash2, StarOff } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';

interface Background {
  id: string;
  name: string;
  isDefault: boolean;
  url: string;
  createdAt: string;
}

export function BackgroundsPage() {
  const qc = useQueryClient();
  const user = useAuth((s) => s.user)!;
  const canEdit = user.isSuperAdmin || user.role === 'TENANT_ADMIN' || user.role === 'MANAGER';
  const backgrounds = useQuery({
    queryKey: ['backgrounds'],
    queryFn: () => api.get<Background[]>('/backgrounds'),
  });

  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [isDefault, setDefault] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const uploadMut = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Pick a file');
      const fd = new FormData();
      fd.append('name', name.trim());
      fd.append('isDefault', String(isDefault));
      fd.append('file', file);
      return api.post('/backgrounds', fd);
    },
    onSuccess: () => {
      toast.success('Background uploaded');
      setOpen(false); setName(''); setFile(null); setDefault(false);
      qc.invalidateQueries({ queryKey: ['backgrounds'] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Upload failed'),
  });

  const setDefaultMut = useMutation({
    mutationFn: (id: string) => api.post(`/backgrounds/${id}/default`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['backgrounds'] }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(`/backgrounds/${id}`),
    onSuccess: () => {
      toast.success('Background deleted');
      qc.invalidateQueries({ queryKey: ['backgrounds'] });
    },
  });

  return (
    <div className="container py-8 space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Backgrounds</h1>
          <p className="text-sm text-muted-foreground">Approved studios that appear in the compositor.</p>
        </div>
        {canEdit && (
          <Button onClick={() => setOpen(true)} className="gap-2">
            <Upload className="h-4 w-4" /> Upload
          </Button>
        )}
      </header>

      {backgrounds.isLoading && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-56" />)}
        </div>
      )}

      {backgrounds.data && backgrounds.data.length === 0 && (
        <Card>
          <CardContent className="py-14 text-center">
            <div className="text-lg font-medium">No backgrounds yet.</div>
            <div className="text-sm text-muted-foreground">Upload your first approved studio.</div>
          </CardContent>
        </Card>
      )}

      {backgrounds.data && backgrounds.data.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {backgrounds.data.map((b) => (
            <Card key={b.id} className="overflow-hidden">
              <div className="relative aspect-[4/3] bg-secondary">
                <img src={b.url} alt={b.name} className="h-full w-full object-cover" />
                {b.isDefault && <Badge className="absolute left-2 top-2" variant="success"><Star className="h-3 w-3 mr-1" />Default</Badge>}
              </div>
              <CardContent className="flex items-center justify-between gap-2 py-3">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{b.name}</div>
                </div>
                {canEdit && (
                  <div className="flex gap-1">
                    {!b.isDefault && (
                      <Button size="icon" variant="ghost" onClick={() => setDefaultMut.mutate(b.id)} title="Set default">
                        <StarOff className="h-4 w-4" />
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => confirm('Delete this background?') && deleteMut.mutate(b.id)} title="Delete">
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload background</DialogTitle>
            <DialogDescription>JPG or PNG. Re-encoded to WebP on upload.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="bg-name">Name</Label>
              <Input id="bg-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Studio — Black" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bg-file">Image</Label>
              <Input id="bg-file" ref={fileInput} type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={isDefault} onChange={(e) => setDefault(e.target.checked)} />
              Set as default background
            </label>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="ghost">Cancel</Button></DialogClose>
            <Button onClick={() => uploadMut.mutate()} disabled={!file || !name.trim() || uploadMut.isPending}>
              {uploadMut.isPending ? 'Uploading…' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
